import { useQuery } from '@tanstack/react-query'
import { api, liveStreamAstrologerName, liveStreamElapsed, liveStreamRows, liveStreamViewerLabel } from '@astro/api'
import { appUrl } from '@astro/config'

/**
 * "Live Right Now" — the main site's band of astrologers currently
 * broadcasting.
 *
 * The twin of apps/astrology/.../liveStream/LiveNowSection.jsx: same data,
 * same numbers (both read the formatting helpers from @astro/api), this app's
 * chrome. The markup is deliberately its own rather than shared, so each home
 * page matches the sections around it.
 *
 * The sessions themselves are watched in the jyotish app, so every card is a
 * cross-app link (full page load) rather than a router <Link>.
 *
 * The band is **always on the page** (Kalyan's call, 2026-11-03): with nobody
 * live it keeps its heading and shows a short "no one is live" panel with a way
 * through to the astrologers, so people learn the feature exists instead of
 * only ever meeting it by chance. The live dot and the heading both change with
 * the state, so an empty band never pretends something is on air.
 */
export default function LiveNowSection() {
  const { data, isPending } = useQuery({
    queryKey: ['live-streams', 'live'],
    queryFn: () => api.liveStreams.list({ status: 'live', per_page: 6 }),
    refetchInterval: 30_000,
    retry: false,
  })

  const streams = liveStreamRows(data)
  const anyLive = streams.length > 0

  return (
    <section id="live-now" className="relative py-20 lg:py-28 overflow-hidden" style={{ background: '#FDFAF6' }}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute -top-32 -left-32 rounded-full"
          style={{ width: '520px', height: '520px', background: 'radial-gradient(circle,rgba(245,210,140,.28) 0%,transparent 70%)', filter: 'blur(70px)' }}
        />
        <div
          className="absolute -bottom-40 -right-24 rounded-full"
          style={{ width: '480px', height: '480px', background: 'radial-gradient(circle,rgba(220,185,120,.22) 0%,transparent 65%)', filter: 'blur(80px)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{ backgroundImage: 'radial-gradient(circle,rgba(200,146,42,.25) 1px,transparent 1px)', backgroundSize: '40px 40px' }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px"
          style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.25),transparent)' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="space-y-5 max-w-2xl mb-10">
          <div
            className="inline-flex items-center gap-3 px-5 py-2 rounded-full"
            style={{ border: '1px solid rgba(200,146,42,.25)', background: 'rgba(200,146,42,.06)' }}
          >
            {anyLive ? (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
              </span>
            ) : (
              /* Quiet gold instead of a pulsing red dot - an empty band must
                 never look like something is on air. */
              <span className="h-2 w-2 rounded-full" style={{ background: 'rgba(200,146,42,.45)' }} />
            )}
            <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
            <span className="text-[10px] uppercase font-semibold tracking-[.35em] font-royal" style={{ color: 'rgba(200,146,42,.8)' }}>
              {anyLive ? 'Live Right Now' : 'Live Sessions'}
            </span>
            <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
            <span className="text-xs leading-none" style={{ color: '#C8922A' }}>✦</span>
          </div>
          <h2 className="font-royal text-3xl sm:text-5xl lg:text-[52px] font-normal tracking-[.03em] leading-[1.15]" style={{ color: '#1C140A' }}>
            {anyLive ? 'Join a Session' : 'Live Sessions'}
            <br />
            <span
              style={{
                background: 'linear-gradient(90deg,#C8922A,#E5A820,#C8922A)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {anyLive ? 'Already in Progress' : 'With Our Astrologers'}
            </span>
          </h2>
          <p className="text-sm sm:text-base font-light leading-relaxed max-w-xl" style={{ color: '#8B7355' }}>
            Our astrologers go live whenever they have something to share. Watching is free and needs no account — sign in only
            if you would like to join the chat.
          </p>
        </div>

        {isPending ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : anyLive ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {streams.map((stream) => (
              <LiveStreamCard key={stream.id} stream={stream} />
            ))}
          </div>
        ) : (
          <EmptyPanel />
        )}
      </div>
    </section>
  )
}

/* Three card-shaped pulses while the first read is in flight. The band is
   always on the page now, so the alternative is the empty panel flashing up
   for a moment and being replaced - which reads as a glitch. */
function SkeletonCard() {
  return (
    <div className="rounded-3xl overflow-hidden animate-pulse" style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.12)' }}>
      <div style={{ aspectRatio: '16/9', background: 'rgba(200,146,42,.10)' }} />
      <div className="p-5 space-y-3">
        <div className="h-4 w-2/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
        <div className="h-2.5 w-1/2 rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
      </div>
    </div>
  )
}

/* Nobody on air. Says so plainly and points at the thing someone can do
   instead, rather than leaving a band with a heading and nothing under it. */
function EmptyPanel() {
  return (
    <div
      className="rounded-3xl px-6 py-12 text-center sm:px-12"
      style={{ background: '#FFF', border: '1px dashed rgba(200,146,42,.3)' }}
    >
      <p className="font-royal text-xl sm:text-2xl" style={{ color: '#1C140A' }}>
        No one is live at the moment
      </p>
      <p className="mx-auto mt-3 max-w-md text-sm font-light leading-relaxed" style={{ color: '#8B7355' }}>
        Sessions start without notice, so do check back. In the meantime you can speak with an astrologer one to one.
      </p>
      <a
        href={appUrl('jyotish', 'consultations')}
        className="mt-7 inline-flex items-center gap-3 rounded-full px-8 py-4 text-[11px] font-bold uppercase tracking-[.22em] transition-all duration-300 hover:scale-105"
        style={{ background: '#1C140A', color: '#FFF', boxShadow: '0 8px 32px -8px rgba(28,20,10,.35)' }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#C8922A'
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = '#1C140A'
        }}
      >
        Meet Our Astrologers
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
        </svg>
      </a>
    </div>
  )
}

function LiveStreamCard({ stream }) {
  const name = liveStreamAstrologerName(stream)
  const photo = stream?.astrologer?.photo_url

  return (
    <a
      href={appUrl('jyotish', `/live/${stream.id}`)}
      className="group relative block rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.18)', boxShadow: '0 8px 32px -16px rgba(28,20,10,.18)' }}
    >
      {/* Streams carry no poster frame, so the astrologer's own photo stands in
          for a preview rather than inventing a thumbnail. */}
      <div className="relative" style={{ aspectRatio: '16/9', background: 'linear-gradient(135deg,#2A1F14,#1C140A)' }}>
        {photo ? (
          <img src={photo} alt={name} className="absolute inset-0 h-full w-full object-cover opacity-80" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center font-royal text-5xl font-bold" style={{ color: 'rgba(200,146,42,.45)' }}>
            {name.trim().charAt(0).toUpperCase()}
          </span>
        )}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[.18em] text-white"
          style={{ background: 'rgba(220,38,38,.92)' }}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
          </span>
          Live
        </span>
        <span className="absolute top-3 right-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">
          {liveStreamViewerLabel(stream.viewer_count)}
        </span>
        <span className="absolute bottom-3 right-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">
          on for {liveStreamElapsed(stream.started_at)}
        </span>
      </div>

      <div className="p-5">
        <h3 className="font-royal text-base sm:text-lg leading-snug line-clamp-2" style={{ color: '#1C140A' }}>
          {stream.title}
        </h3>
        <p className="mt-1.5 text-xs font-light" style={{ color: '#8B7355' }}>
          with {name}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.2em]" style={{ color: '#C8922A' }}>
          Watch now
          <svg className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </span>
      </div>
    </a>
  )
}
